import React, { useState } from "react";
import { View, StyleSheet, TextInput, Platform } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import type { OverrideStringEditorProps } from "./types";

export function OverrideStringEditor({
  value,
  label,
  originalKey,
  overrideKey,
  onSave,
  onCancel,
}: OverrideStringEditorProps) {
  const { colors } = useTheme();

  const data =
    typeof value === "string"
      ? (() => {
          try {
            return JSON.parse(value);
          } catch {
            return {};
          }
        })()
      : value || {};

  const original = data[originalKey] || "";
  const currentOverride = data[overrideKey] || "";

  const [overrideText, setOverrideText] = useState<string>(currentOverride);
  const [focused, setFocused] = useState(false);

  const isModified = overrideText.trim() !== "";

  const handleRevert = () => {
    setOverrideText("");
  };

  const handleApply = () => {
    onSave(overrideText.trim() || null);
  };

  return (
    <View style={styles.container}>
      {(label || isModified) && (
        <View style={styles.labelRow}>
          {label ? <ThemedText type="subtitle">{label}</ThemedText> : <View />}
          {isModified && (
            <Button
              text="Revert"
              icon="undo"
              iconSize={12}
              style={[styles.revertButton, { backgroundColor: colors.amber.background }]}
              textStyle={[styles.revertButtonText, { color: colors.amber.text }]}
              iconStyle={{ color: colors.amber.text }}
              onPress={handleRevert}
            />
          )}
        </View>
      )}

      <View style={styles.originalRow}>
        <ThemedText style={[styles.originalLabel, { color: colors.placeholder }]}>Original:</ThemedText>
        <ThemedText style={[styles.originalValue, { color: colors.text }]}>{original || "\u2014"}</ThemedText>
      </View>

      <View style={styles.overrideRow}>
        <ThemedText style={[styles.overrideLabel, { color: colors.placeholder }]}>Override:</ThemedText>
        <View
          style={[
            styles.inputWrapper,
            {
              borderColor: focused ? colors.primary : isModified ? colors.amber.border : colors.border,
            },
          ]}
        >
          <TextInput
            style={[styles.input, { color: colors.text }]}
            value={overrideText}
            onChangeText={setOverrideText}
            placeholder={original || "Enter override"}
            placeholderTextColor={colors.placeholder}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
        </View>
      </View>

      <View style={styles.footer}>
        <Button
          text="Cancel"
          style={[styles.cancelButton, { borderColor: colors.border }]}
          textStyle={{ color: colors.text }}
          onPress={onCancel}
        />
        <Button
          text="Apply"
          style={[styles.applyButton, { backgroundColor: colors.primary }]}
          textStyle={{ color: "white" }}
          onPress={handleApply}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: "transparent",
    padding: 8,
    gap: 8,
    minWidth: 280,
    maxWidth: 360,
    overflow: "visible",
  },
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  revertButton: {
    height: 24,
    paddingHorizontal: 8,
    gap: 4,
    borderRadius: 4,
  },
  revertButtonText: {
    fontSize: 11,
    fontWeight: "600",
  },
  originalRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  originalLabel: {
    fontSize: 12,
    width: 60,
  },
  originalValue: {
    fontSize: 13,
    fontWeight: "500",
  },
  overrideRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  overrideLabel: {
    fontSize: 12,
    width: 60,
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 10,
    height: 36,
    flex: 1,
  },
  input: {
    flex: 1,
    fontSize: 14,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  footer: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 8,
  },
  cancelButton: {
    height: 36,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  applyButton: {
    height: 36,
    paddingHorizontal: 16,
  },
});
